import type { ColumnDef } from "@tanstack/react-table";
import { buildActionColumn, buildIconTextColumn, buildTagColumn } from "@/components/Table";
import { renderEmptyCellState } from "@/components/Table/utils";
import { formatInteractionDisplayDate } from "@/components/interactions";
import { actionId } from "@/hooks/useTableRowMenu";
import { canOpenTimelineRow, PersonTimelineObjectType, type PersonTimelineRow } from "../types";

const typeLabels: Record<PersonTimelineObjectType, string> = {
  [PersonTimelineObjectType.NOTE]: "Note",
  [PersonTimelineObjectType.INTERACTION]: "Interaction"
};

const typeTagTypes: Record<PersonTimelineObjectType, "secondary" | "information"> = {
  [PersonTimelineObjectType.NOTE]: "information",
  [PersonTimelineObjectType.INTERACTION]: "secondary"
};

const DESCRIPTION_PREVIEW_LENGTH = 250;

interface ColumnsOptions {
  onViewRow: (row: PersonTimelineRow) => void;
  sortable?: boolean;
}

export function getColumns({ onViewRow, sortable = true }: ColumnsOptions): ColumnDef<PersonTimelineRow, unknown>[] {
  return [
    buildTagColumn<PersonTimelineRow>({
      accessorKey: "objectType",
      header: "Type",
      getTagLabel: (row) => typeLabels[row.objectType],
      getTagType: (row) => typeTagTypes[row.objectType],
      getTagStyle: () => "standard",
      getTagExtras: (row) =>
        row.isHighlight
          ? {
              iconName: "star",
              tooltipText: "Highlighted Milestone"
            }
          : {},
      size: 130,
      ...(sortable ? { sortType: "text" as const } : { enableSorting: false })
    }),
    buildIconTextColumn<PersonTimelineRow>({
      accessorKey: "authorName",
      header: "Author",
      getText: (row) => row.author?.name || "--",
      size: 140,
      ...(sortable ? { sortType: "text" as const } : { enableSorting: false })
    }),
    buildIconTextColumn<PersonTimelineRow>({
      accessorKey: "title",
      header: "Activity",
      getText: (row) => row.title,
      getIcon: (row) =>
        row.isPrivate
          ? {
              name: "lock",
              size: 20,
              tooltip: {
                supportingText: "Content is Private"
              }
            }
          : null,
      iconPosition: "after",
      size: 240,
      minSize: 220,
      ...(sortable ? { sortType: "text" as const } : { enableSorting: false }),
      meta: { headerClassName: "hide-header-in-column" }
    }),
    {
      accessorKey: "description",
      header: "Comment",
      size: 320,
      cell: ({ row }) => {
        const description = row.original.description || "";
        const truncated =
          description.length > DESCRIPTION_PREVIEW_LENGTH
            ? `${description.substring(0, DESCRIPTION_PREVIEW_LENGTH)}...`
            : description;

        return renderEmptyCellState(truncated);
      },
      ...(sortable ? { meta: { sortType: "text" as const } } : { enableSorting: false })
    },
    {
      accessorKey: "occurredAt",
      header: "Occurred",
      size: 140,
      cell: ({ row }) => renderEmptyCellState(formatInteractionDisplayDate(row.original.occurredAt)),
      ...(sortable ? { meta: { sortType: "date" as const } } : { enableSorting: false })
    },
    buildActionColumn<PersonTimelineRow>({
      onViewClick: onViewRow,
      showViewButton: canOpenTimelineRow,
      isRowNavigable: canOpenTimelineRow,
      viewButtonTitle: "View Details",
      actionId,
      setMenu: () => {}
    })
  ];
}
